import React from "react";
import { Box, Typography, Stack, Button, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BsArrowRight, BsGlobe, BsGithub, BsFilePdf } from "react-icons/bs";
import { TbTopologyStar3, TbUsers } from "react-icons/tb";
import { colors, font, pagePx } from "../theme/tokens";
import SectionHeading from "../components/SectionHeading";
import Reveal from "../components/Reveal";

// ---- Professional projects (freelance / client work) ----
const projects = [
  {
    title: "Ordertech",
    tag: "AI SaaS",
    subtitle: "AI voice ordering platform for restaurants",
    description:
      "An AI-powered platform that automates restaurant order-taking over voice, cutting labor cost and lifting revenue through intelligent upselling and 24/7 availability.",
    highlights: ["99.2% order accuracy", "Real-time dashboard", "POS integrations (Foodics, Odoo)"],
    tech: ["NestJS", "PostgreSQL", "Prisma", "React", "TypeScript"],
    links: [{ label: "ordertech.ai", url: "https://ordertech.ai", icon: <BsGlobe /> }],
    accent: colors.accent,
  },
  {
    title: "Hawiacom",
    tag: "Full-stack SaaS",
    subtitle: "Container-rental management platform",
    description:
      "An end-to-end SaaS for container-rental companies: customer portal, an integrated accounting ERP, and a mobile driver app for real-time delivery management.",
    highlights: ["Admin dashboard + ERP", "Flutter driver app", "Real-time order tracking"],
    tech: ["Node.js", "MySQL", "Sequelize", "React", "Flutter"],
    links: [{ label: "hawiacom.com", url: "https://hawiacom.com", icon: <BsGlobe /> }],
    accent: colors.green,
  },
];

// ---- Academic flagship case studies ----
const academic = [
  {
    id: "virtufit",
    route: "/projects/virtufit",
    title: "VirtuFit",
    subtitle: "AI Virtual Try-On Platform · 8 microservices, event-driven",
    image: "/assets/vf_tryon.jpg",
    description:
      "A generic, extensible virtual try-on platform architected as eight polyglot microservices (FastAPI + NestJS) with Kafka event streaming, CQRS read-models, circuit breakers, and a hot-swappable AI model registry with automatic fallback — plus a secured B2B API.",
    metrics: [
      { k: "8", v: "microservices" },
      { k: "7", v: "AI providers + fallback" },
      { k: "7+", v: "design patterns" },
    ],
    tech: ["FastAPI", "NestJS", "Apache Kafka", "PostgreSQL", "Redis", "MinIO / S3", "React", "Docker"],
    focus: "Software Architecture",
  },
  {
    id: "orchestrix",
    route: "/projects/orchestrix",
    title: "Orchestrix",
    subtitle: "AI Conversational Commerce Platform · WhatsApp-native",
    image: "/assets/orx_dashboard.jpg",
    description:
      "A SaaS platform that runs an entire e-commerce ordering journey over WhatsApp through a stateful LangGraph AI agent — multimodal (text, image, voice), with human-in-the-loop handoff, deep Shopify sync, and a full merchant dashboard.",
    metrics: [
      { k: "LangGraph", v: "10-node state graph" },
      { k: "WhatsApp", v: "via Twilio" },
      { k: "Shopify", v: "bi-directional sync" },
    ],
    tech: ["NestJS", "LangGraph", "GPT-4o-mini", "Twilio", "Shopify API", "PostgreSQL", "React"],
    focus: "AI Agents & Integrations",
  },
];

const Projects = () => {
  const navigate = useNavigate();

  return (
    <Box component="section" id="work" sx={{ px: pagePx, py: { xs: 8, md: 12 }, backgroundColor: colors.bgAlt, position: "relative" }}>
      <SectionHeading
        eyebrow="Selected Work"
        title="Things I've designed and shipped"
        subtitle="Production SaaS for real clients, and two deep academic case studies in architecture and applied AI."
      />

      {/* ===== Professional projects ===== */}
      <Typography sx={{ fontFamily: font.mono, fontSize: 12.5, letterSpacing: 2, textTransform: "uppercase", color: colors.inkMute, mb: 2.5 }}>
        // Professional
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: { xs: 7, md: 9 } }}>
        {projects.map((p, i) => (
          <Reveal key={p.title} delay={i * 90}>
            <Box
              sx={{
                height: "100%", display: "flex", flexDirection: "column",
                p: 3.5, borderRadius: "16px", backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`, transition: "all 0.25s ease",
                "&:hover": { transform: "translateY(-5px)", borderColor: `${p.accent}88`, boxShadow: "0 18px 44px rgba(0,0,0,0.4)" },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Chip icon={<TbTopologyStar3 size={13} />} label={p.tag} size="small"
                  sx={{ backgroundColor: `${p.accent}1f`, color: p.accent, fontWeight: 600, fontSize: 11, "& .MuiChip-icon": { color: p.accent } }} />
              </Stack>
              <Typography sx={{ fontFamily: font.sans, fontWeight: 700, fontSize: 22, color: colors.ink }}>{p.title}</Typography>
              <Typography sx={{ fontSize: 14, color: p.accent, fontWeight: 500, mb: 1.5 }}>{p.subtitle}</Typography>
              <Typography sx={{ fontSize: 14.5, color: colors.inkSoft, lineHeight: 1.7, mb: 2 }}>{p.description}</Typography>

              <Box sx={{ mb: 2 }}>
                {p.highlights.map((h) => (
                  <Typography key={h} sx={{ fontSize: 13, color: colors.inkSoft, mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                    <Box component="span" sx={{ color: p.accent }}>▸</Box> {h}
                  </Typography>
                ))}
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mb: 2.5 }}>
                {p.tech.map((t) => (
                  <Box key={t} sx={{ fontFamily: font.mono, fontSize: 11, color: colors.inkMute, border: `1px solid ${colors.border}`, borderRadius: "6px", px: 0.9, py: 0.3 }}>{t}</Box>
                ))}
              </Box>

              <Stack direction="row" spacing={1} sx={{ mt: "auto", flexWrap: "wrap", gap: 1 }}>
                {p.links.map((l) => (
                  <Box key={l.label} component="a" href={l.url} target="_blank" rel="noopener noreferrer"
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.6, fontSize: 12.5, fontWeight: 500,
                      color: colors.ink, border: `1px solid ${colors.borderStrong}`, borderRadius: "8px", px: 1.2, py: 0.6,
                      transition: "all 0.2s ease", "&:hover": { borderColor: p.accent, color: p.accent }, "& svg": { fontSize: 13 },
                    }}
                  >
                    {l.icon} {l.label}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Reveal>
        ))}
      </Box>

      {/* ===== Academic case studies ===== */}
      <Typography sx={{ fontFamily: font.mono, fontSize: 12.5, letterSpacing: 2, textTransform: "uppercase", color: colors.inkMute, mb: 2.5 }}>
        // Academic — Final-Year Capstone (PFE)
      </Typography>

      <Stack spacing={4}>
        {academic.map((a, idx) => (
          <Reveal key={a.id}>
            <Box
              sx={{
                position: "relative", borderRadius: "22px", overflow: "hidden",
                border: "1px solid rgba(99,102,241,0.32)",
                background: "linear-gradient(135deg, rgba(99,102,241,0.11) 0%, rgba(34,211,238,0.05) 100%)",
              }}
            >
              <Box className="aurora" sx={{ width: 300, height: 300, background: "#4f46e5", top: -100, right: idx % 2 ? "auto" : -60, left: idx % 2 ? -60 : "auto", opacity: 0.35 }} />
              <Stack direction={{ xs: "column", md: idx % 2 ? "row-reverse" : "row" }} sx={{ position: "relative", zIndex: 1 }}>
                {/* copy */}
                <Box sx={{ flex: 1, p: { xs: 3, md: 4.5 } }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
                    <Chip label="Case Study" size="small" sx={{ background: colors.gradPrimary, color: "#0b1120", fontWeight: 700, fontFamily: font.mono, fontSize: 11 }} />
                    <Chip label={a.focus} size="small" sx={{ backgroundColor: "rgba(255,255,255,0.06)", color: colors.inkSoft, border: `1px solid ${colors.border}`, fontSize: 11 }} />
                  </Stack>

                  <Typography sx={{ fontFamily: font.sans, fontWeight: 800, fontSize: { xs: 26, md: 30 }, color: colors.ink, mb: 0.5 }}>{a.title}</Typography>
                  <Typography sx={{ fontSize: 14.5, color: colors.accent, fontWeight: 600, mb: 2 }}>{a.subtitle}</Typography>
                  <Typography sx={{ fontSize: 15, color: colors.inkSoft, lineHeight: 1.7, mb: 3, maxWidth: 560 }}>{a.description}</Typography>

                  <Stack direction="row" spacing={3} sx={{ mb: 3, flexWrap: "wrap", gap: 2 }}>
                    {a.metrics.map((m) => (
                      <Box key={m.v}>
                        <Typography sx={{ fontFamily: font.sans, fontWeight: 800, fontSize: m.k.length > 4 ? 17 : 24, color: colors.ink, lineHeight: 1.2 }}>{m.k}</Typography>
                        <Typography sx={{ fontSize: 12, color: colors.inkMute }}>{m.v}</Typography>
                      </Box>
                    ))}
                  </Stack>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 3 }}>
                    {a.tech.map((t) => (
                      <Box key={t} sx={{ fontFamily: font.mono, fontSize: 12, color: colors.inkSoft, border: `1px solid ${colors.border}`, borderRadius: "7px", px: 1.1, py: 0.4 }}>{t}</Box>
                    ))}
                  </Box>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button endIcon={<BsArrowRight />} onClick={() => navigate(a.route)}
                      sx={{ background: colors.gradPrimary, color: "#0b1120", fontFamily: font.sans, fontWeight: 700, fontSize: 15, textTransform: "none", px: 3, py: 1.2, borderRadius: "12px", boxShadow: "0 8px 30px rgba(99,102,241,0.3)", "&:hover": { transform: "translateY(-2px)" }, transition: "all 0.2s ease" }}>
                      Read the case study
                    </Button>
                    <Button startIcon={<BsFilePdf />} href="/Rapport_PFE.pdf" target="_blank" rel="noopener noreferrer"
                      sx={{ color: colors.ink, fontFamily: font.sans, fontWeight: 600, fontSize: 14.5, textTransform: "none", px: 2.5, py: 1.2, borderRadius: "12px", border: `1px solid ${colors.borderStrong}`, "&:hover": { borderColor: colors.accent, color: colors.accent } }}>
                      Full report (PDF)
                    </Button>
                  </Stack>
                </Box>

                {/* screenshot */}
                <Box sx={{ flex: { md: "0 0 40%" }, p: { xs: 3, md: 4 }, display: "flex", alignItems: "center" }}>
                  <Box component="img" src={a.image} alt={`${a.title} interface`}
                    sx={{ width: "100%", borderRadius: "14px", border: `1px solid ${colors.borderStrong}`, boxShadow: "0 24px 60px rgba(0,0,0,0.5)", display: "block" }} />
                </Box>
              </Stack>
            </Box>
          </Reveal>
        ))}

        {/* Team academic project note */}
        <Reveal>
          <Box sx={{ p: 3, borderRadius: "16px", backgroundColor: colors.surface, border: `1px solid ${colors.border}`, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Chip icon={<TbUsers size={14} />} label="Team project" size="small" sx={{ backgroundColor: `${colors.amber}1f`, color: colors.amber, fontWeight: 600, fontSize: 11, "& .MuiChip-icon": { color: colors.amber } }} />
            <Typography sx={{ fontSize: 14, color: colors.inkSoft, flex: 1, minWidth: 240 }}>
              <Box component="span" sx={{ color: colors.ink, fontWeight: 600 }}>Final-Year Project Platform</Box> — a 9-microservice academic project-management system (Spring Boot + Node.js, Dockerized), built with a multidisciplinary team.
            </Typography>
            <Box component="a" href="https://github.com/kadri-ayoub-2022/projet-cs-2" target="_blank" rel="noopener noreferrer"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, fontSize: 12.5, fontWeight: 500, color: colors.ink, border: `1px solid ${colors.borderStrong}`, borderRadius: "8px", px: 1.4, py: 0.7, "&:hover": { borderColor: colors.accent, color: colors.accent } }}>
              <BsGithub /> GitHub
            </Box>
          </Box>
        </Reveal>
      </Stack>
    </Box>
  );
};

export default Projects;
